import { type ColDef, ModuleRegistry } from "ag-grid-enterprise";
import { AgGridReact, type AgGridReactProps } from "ag-grid-react";
import "ag-grid-enterprise";
import { useMemo } from "react";
import { RowGroupingModule, RowGroupingPanelModule } from "ag-grid-enterprise";
import clsx from "clsx";

ModuleRegistry.registerModules([RowGroupingModule, RowGroupingPanelModule]);

export interface AgGridRowGroupingProps<TData> extends Omit<AgGridReactProps<TData>, "autoGroupColumnDef"> {
	id?: string;
	className?: string;
	groupField: string;
	groupColumnDef?: ColDef;
	aggregationFunctions?: Record<string, (params: any) => any>;
	groupDefaultExpanded?: number;
	showGroupPanel?: boolean;
	disableFiltering?: boolean;
	suppressMenu?: boolean;
	icons?: AgGridReactProps<TData>["icons"];
	columnDefs: (ColDef<TData> & {
		rowGroup?: boolean;
		hide?: boolean;
		aggFunc?: (params: any) => any;
	})[];
}

export const AgGridRowGrouping = <TData,>(props: AgGridRowGroupingProps<TData>) => {
	const {
		id,
		className,
		rowData,
		columnDefs,
		groupField,
		groupColumnDef = {},
		aggregationFunctions = {},
		groupDefaultExpanded = 0,
		showGroupPanel = false,
		disableFiltering = false,
		icons,
		...rest
	} = props;

	const { cellRenderer: innerRenderer, cellRendererParams: innerRendererParams, ...restOfGroupColDef } = groupColumnDef;

	const enhancedColumnDefs = useMemo(
		() =>
			columnDefs.map((cd) => {
				const newColDef = { ...cd };
				if (aggregationFunctions[cd.field as string]) {
					newColDef.aggFunc = aggregationFunctions[cd.field as string];
				}
				if (cd.field === groupField) {
					newColDef.rowGroup = true;
					newColDef.hide = true;
				}
				return newColDef;
			}),
		[columnDefs, aggregationFunctions, groupField],
	);

	const autoGroupColumnDef: ColDef = {
		headerName: "Group",
		minWidth: 250,
		...restOfGroupColDef,
		cellRenderer: "agGroupCellRenderer",
		cellRendererParams: {
			suppressCount: false,
			innerRenderer: innerRenderer,
			...innerRendererParams,
		},
	};
	const defaultConfig = {
		defaultColDef: {
			flex: 1,
			sortable: true,
			filter: !disableFiltering,
			resizable: true,
		},
		groupDefaultExpanded,
		rowGroupPanelShow: (showGroupPanel ? "always" : "never") as "always" | "never",
		suppressAggFuncInHeader: true,
		animateRows: true,
		rowHeight: 50,
		suppressRowClickSelection: true,
	};
	return (
		<div
			id={id}
			className={clsx({
				[`${className}`]: className,
			})}
		>
			<AgGridReact<TData>
				{...defaultConfig}
				{...rest}
				rowData={rowData}
				columnDefs={enhancedColumnDefs}
				autoGroupColumnDef={autoGroupColumnDef}
				icons={icons}
			/>
		</div>
	);
};
